import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HiOutlineMail,
  HiArrowRight,
  HiCheckCircle,
  HiExclamationCircle,
} from "react-icons/hi";
import { AiOutlineLoading } from "react-icons/ai";

import dosee_logo from "../../../assets/images/dosee.png"; // Adjust path as needed
import { useAdminContext } from "../../context/admin/context/AdminContext";

const VerifyInstructions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resendOtp } = useAdminContext(); // Get resend function

  // Retrieve the email passed from the signup page
  const email = location.state?.email || "your email";

  // Local State for Resend Logic
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, text: null });

  const handleResend = async () => {
    setLoading(true);
    setFeedback({ type: null, text: null });

    try {
      const response = await resendOtp({ email });

      if (response.status === 200) {
        setFeedback({ type: "success", text: "New code sent successfully!" });
      } else {
        setFeedback({
          type: "error",
          text: response.message || "Failed to resend code. Please try again.",
        });
      }
    } catch (error) {
      setFeedback({
        type: "error",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-gray-50 text-gray-900 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div
        className="fixed inset-0 bg-[radial-gradient(1200px_600px_at_50%_0%,rgba(16,185,129,0.05),transparent_60%)] pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md">
        {/* Glow Effect */}
        <div
          className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-emerald-200/50 via-amber-200/50 to-purple-200/50 blur-xl"
          aria-hidden="true"
        />

        <div className="relative rounded-3xl border border-gray-200 bg-white p-8 shadow-xl text-center">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img src={dosee_logo} alt="Dosee Africa" className="h-10 w-auto" />
          </div>

          {/* Icon */}
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/50">
            <HiOutlineMail className="h-10 w-10" />
          </div>

          {/* Heading */}
          <h1 className="mb-3 text-2xl font-bold tracking-tight text-gray-900">
            Check your inbox
          </h1>

          {/* Message */}
          <p className="mb-6 text-sm leading-relaxed text-gray-600">
            We have sent a verification code to <br />
            <span className="font-semibold text-gray-900">{email}</span>
          </p>

          <div className="rounded-lg bg-gray-50 p-4 mb-6 border border-gray-100">
            <p className="text-xs text-gray-500">
              Please check your email, copy the 6-digit OTP code, and enter it
              on the verification screen to activate your company account.
            </p>
          </div>

          {/* Feedback Message (Success/Error) */}
          {feedback.text && (
            <div
              className={`mb-4 p-3 rounded-lg flex items-center justify-center gap-2 text-xs font-medium ${
                feedback.type === "success"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {feedback.type === "success" ? (
                <HiCheckCircle className="h-4 w-4" />
              ) : (
                <HiExclamationCircle className="h-4 w-4" />
              )}
              {feedback.text}
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={() => navigate("/verify-otp", { state: { email } })}
            className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <span className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
            <span>Enter Verification Code</span>
            <HiArrowRight className="h-4 w-4" />
          </button>

          {/* Footer / Resend */}
          <div className="mt-6 text-xs text-gray-500">
            Did not receive the email?{" "}
            <button
              onClick={handleResend}
              disabled={loading}
              className="font-medium text-emerald-600 hover:text-emerald-500 hover:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 align-bottom"
            >
              {loading ? (
                <>
                  <AiOutlineLoading className="animate-spin" /> Sending...
                </>
              ) : (
                "Click to resend"
              )}
            </button>
            <br />
            <span className="mt-2 block">
              Wrong email?{" "}
              <Link
                to="/signup"
                className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900"
              >
                Register again
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyInstructions;
